// ブランドグループ照会と entrydata への反映/クリアをまとめる。
// 本文は HinbanMaster.js から移設したもの。

export function createBrandGroupService(deps) {
    const {
        DicVal,
        FIXED_HINBAN_NUMBERING_COLUMNID,
        applyFirstExistingValue,
        applyFixedHinbanNumberingValue,
        assignEntryDataCurrent,
        brandGroupInfoRequestSeqRef,
        dispatch,
        entrydata,
        entrydataRef,
        entryrerender,
        fnc,
        isEmpty,
        lastBrandGroupBrandCodeRef,
        protectHbSeasonCommit,
        rerender,
        resolveBrandGroupBrandCode,
        setEntryDataValue
    } = deps;

    function applyBrandGroupInfoToEntryData(targetData, brandGroupInfo) {
        if (isEmpty(targetData) || isEmpty(brandGroupInfo)) return false;
        let updated = false;
        const brandGroupCode = DicVal(brandGroupInfo, "BRG_CODE", "");
        const brandGroupName = DicVal(brandGroupInfo, "BRG_NAME", "");
        const setDirectValue = (key, value) => {
            const text = String(isEmpty(value) ? "" : value);
            if (String(DicVal(targetData, key, "")) === text) return false;
            targetData[key] = text;
            return true;
        };
        const mappings = [
            { targets: ["m_code_attra", "PDT_BRGATTRA"], value: DicVal(brandGroupInfo, "m_code_attra", "") },
            { targets: ["BRG_CODE", "PDT_BRGCODE", "PDT_BRG_CODE", "BRANDGP_CODE", "PDTMD_BRDGP", "PDTMD_BRDGP_cd"], value: brandGroupCode },
            { targets: ["BRG_NAME", "PDT_BRGNAME", "PDT_BRG_NAME", "BRANDGP_NAME", "PDTMD_BRDGP_nm"], value: brandGroupName },
        ];
        if (applyFixedHinbanNumberingValue(targetData, brandGroupInfo)) updated = true;
        mappings.forEach((mapping) => {
            if (applyFirstExistingValue(targetData, mapping.targets, mapping.value)) {
                updated = true;
            }
        });
        if (setDirectValue("PDTMD_BRDGP_cd", brandGroupCode)) updated = true;
        if (setDirectValue("PDTMD_BRDGP_nm", brandGroupName)) updated = true;
        if (setDirectValue("PDTMD_BRDGP", brandGroupCode)) updated = true;
        return updated;
    }
    function clearBrandGroupInfoOnEntryData(targetData, options) {
        const clearOptions = isEmpty(options) ? {} : options;
        if (isEmpty(targetData)) return false;
        let updated = false;
        const clearDirectValue = (key) => {
            if (String(DicVal(targetData, key, "")) === "") return false;
            targetData[key] = "";
            return true;
        };
        [
            "m_code_attra",
            "PDT_BRGATTRA",
            "BRG_CODE",
            "PDT_BRGCODE",
            "PDT_BRG_CODE",
            "BRANDGP_CODE",
            "PDTMD_BRDGP_cd",
            "PDTMD_BRDGP",
            "BRG_NAME",
            "PDT_BRGNAME",
            "PDT_BRG_NAME",
            "BRANDGP_NAME",
            "PDTMD_BRDGP_nm"
        ].forEach((key) => {
            if (!(key in targetData)) return;
            if (String(DicVal(targetData, key, "")) === "") return;
            targetData[key] = "";
            updated = true;
        });
        if (clearDirectValue("PDTMD_BRDGP_cd")) updated = true;
        if (clearDirectValue("PDTMD_BRDGP_nm")) updated = true;
        if (clearDirectValue("PDTMD_BRDGP")) updated = true;
        if (DicVal(clearOptions, "clearFixedHinbanNumbering", true)
            && setEntryDataValue(targetData, FIXED_HINBAN_NUMBERING_COLUMNID, "0")) updated = true;
        return updated;
    }
    function loadBrandGroupInfo(options) {
        const requestOptions = isEmpty(options) ? {} : options;
        const explicitTargetData = DicVal(requestOptions, "targetData", null);
        const targetData = isEmpty(explicitTargetData)
            ? entrydataRef.current
            : explicitTargetData;
        if (isEmpty(targetData)) return;

        const brandCode = String(resolveBrandGroupBrandCode(targetData)).trim();
        if (isEmpty(brandCode)) {
            brandGroupInfoRequestSeqRef.current += 1;
            lastBrandGroupBrandCodeRef.current = "";
            const cleared = clearBrandGroupInfoOnEntryData(targetData);
            if (!cleared) return;
            assignEntryDataCurrent(protectHbSeasonCommit(targetData, "targetData"), "targetData");
            dispatch(entrydata({ ...entrydataRef.current }));
            dispatch(entryrerender());
            return;
        }
        if (!DicVal(requestOptions, "force", false) && lastBrandGroupBrandCodeRef.current === brandCode) {
            return;
        }
        const requestSeq = brandGroupInfoRequestSeqRef.current + 1;
        brandGroupInfoRequestSeqRef.current = requestSeq;
        lastBrandGroupBrandCodeRef.current = brandCode;

        const resolveBrandGroupCommitTarget = () => {
            if (!isEmpty(explicitTargetData)) {
                return String(resolveBrandGroupBrandCode(explicitTargetData)).trim() === brandCode ? explicitTargetData : null;
            }
            if (isEmpty(entrydataRef.current)) return null;
            return String(resolveBrandGroupBrandCode(entrydataRef.current)).trim() === brandCode ? entrydataRef.current : null;
        };

        fnc.current.postaxios("hinbanmaster/GetBrandGroupName",
            { PDT_BRDCODE: brandCode },
            {
                onSuccess: function (data) {
                    if (brandGroupInfoRequestSeqRef.current !== requestSeq) return;
                    const commitTargetData = resolveBrandGroupCommitTarget();
                    if (isEmpty(commitTargetData)) return;
                    let changed = false;
                    if (String(DicVal(data.returnData, "Found", "false")).toLowerCase() === "true") {
                        changed = applyBrandGroupInfoToEntryData(commitTargetData, data.returnData);
                    } else if (DicVal(requestOptions, "clearOnNotFound", true)) {
                        changed = clearBrandGroupInfoOnEntryData(commitTargetData, { clearFixedHinbanNumbering: false });
                    }
                    if (changed) {
                        assignEntryDataCurrent(protectHbSeasonCommit(commitTargetData, "commitTargetData"), "commitTargetData");
                    } else if (!DicVal(requestOptions, "rerenderOnComplete", false)) {
                        return;
                    }
                    dispatch(entrydata({ ...entrydataRef.current }));
                    dispatch(entryrerender());
                },
                onError: function (data) {
                    if (brandGroupInfoRequestSeqRef.current !== requestSeq) return;
                    lastBrandGroupBrandCodeRef.current = "";
                    if (!DicVal(requestOptions, "silent", true)) {
                        let msg = "ブランドグループ情報の取得に失敗しました";
                        if (!isEmpty(data.errDescription)) msg = data.errDescription;
                        fnc.current.warnmsg(msg);
                    }
                }
            }, true
        );
    }

    return {
        applyBrandGroupInfoToEntryData,
        clearBrandGroupInfoOnEntryData,
        loadBrandGroupInfo
    };
}
